/**
 * RabbitDemoPanel — C057 RabbitMQ 메시징 라이브 데모
 * 계약: docs/common-specs/eraf-messaging-rabbitmq/C057-rabbitmq-messaging.md §Δ.3 / §Δ.6
 * C055(KafkaDemoPanel) 동형 패턴 — status 카드 → 메시지 발행 → 수신 왕복 확인.
 * 5상태: loading(상태조회 skeleton) / empty(발행 전 · 수신 전) / error(상태조회 실패 · 발행 실패) /
 *        default(상태 + 발행결과 + 수신목록) / dialog(N/A — 인라인 콘솔, Kafka 동형)
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRabbitStatus } from '../hooks/useRabbitDemo';
import { useRabbitPublish } from '../hooks/useRabbitDemo';
import { useRabbitReceived } from '../hooks/useRabbitDemo';
import { rabbitFormSchema, type RabbitFormValues } from '../schema/showcaseSchema';
import type { ApiError } from '../types/showcase';

export function RabbitDemoPanel() {
  const { t } = useTranslation();
  const statusQuery = useRabbitStatus();
  const publishMutation = useRabbitPublish();
  const receivedQuery = useRabbitReceived(20);

  const [publishError, setPublishError] = useState<ApiError | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<RabbitFormValues>({
    resolver: zodResolver(rabbitFormSchema),
    defaultValues: { message: '' },
  });

  // 발행 성공 후 자동 1회 폴링 — 컨슈머 왕복 지연을 감안해 짧게 지연 후 조회
  useEffect(() => {
    if (!publishMutation.isSuccess) return;
    const timer = setTimeout(() => {
      void receivedQuery.refetch();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, 800);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publishMutation.data]);

  const status = statusQuery.data;
  const isStatusLoading = statusQuery.isLoading;
  const isStatusError = statusQuery.isError;
  const disabled = !status?.enabled;

  function onSubmit(values: RabbitFormValues) {
    setPublishError(null);
    publishMutation.mutate(
      { message: values.message },
      {
        onSuccess: () => reset({ message: '' }),
        onError: (err) => setPublishError(err),
      }
    );
  }

  return (
    <section aria-labelledby="rabbitmq-panel-title">
      <h2
        id="rabbitmq-panel-title"
        style={{
          fontSize: 'var(--font-size-lg)',
          fontWeight: 700,
          color: 'var(--color-text-primary)',
          marginBottom: 'var(--spacing-2)',
          marginTop: 0,
        }}
      >
        {t('showcase.rabbitmq.title')}
      </h2>
      <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--spacing-6)' }}>
        {t('showcase.rabbitmq.subtitle')}
      </p>

      {/* status — loading skeleton */}
      {isStatusLoading && (
        <div aria-busy="true" aria-label={t('showcase.rabbitmq.status.loadingAriaLabel')} style={cardStyle}>
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              style={{
                height: 16,
                width: `${35 + ((i * 15) % 40)}%`,
                borderRadius: 'var(--radius-sm)',
                background: 'var(--color-surface-elevated)',
                animation: 'pulse 1.5s ease-in-out infinite',
                marginBottom: 'var(--spacing-2)',
              }}
            />
          ))}
        </div>
      )}

      {/* status — error */}
      {isStatusError && (
        <div role="alert" style={{ ...cardStyle, border: '1px solid var(--color-danger)' }}>
          <p style={{ margin: 0, color: 'var(--color-danger)', fontSize: 'var(--font-size-sm)' }}>
            {(statusQuery.error as ApiError)?.message ?? t('showcase.rabbitmq.status.loadFailed')}
          </p>
          <button type="button" onClick={() => void statusQuery.refetch()} style={secondaryButtonStyle}>
            {t('showcase.common.retry')}
          </button>
        </div>
      )}

      {/* status — default */}
      {!isStatusLoading && !isStatusError && status && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}>
          <div style={cardStyle}>
            <h3 style={sectionTitleStyle}>{t('showcase.rabbitmq.status.title')}</h3>
            <div style={infoGridStyle}>
              <InfoField label={t('showcase.rabbitmq.status.enabled')}>
                <span style={{ color: status.enabled ? 'var(--color-status-success)' : 'var(--color-status-error)', fontWeight: 700 }}>
                  {status.enabled ? t('showcase.rabbitmq.status.enabledYes') : t('showcase.rabbitmq.status.enabledNo')}
                </span>
              </InfoField>
              <InfoField label={t('showcase.rabbitmq.status.exchange')}>
                <code style={monoStyle}>{status.exchange}</code>
              </InfoField>
              <InfoField label={t('showcase.rabbitmq.status.queue')}>
                <code style={monoStyle}>{status.queue}</code>
              </InfoField>
              <InfoField label={t('showcase.rabbitmq.status.broker')}>
                <code style={monoStyle}>{status.broker}</code>
              </InfoField>
              <InfoField label={t('showcase.rabbitmq.status.consumerRunning')}>
                <span style={{ color: status.consumerRunning ? 'var(--color-status-success)' : 'var(--color-text-muted)' }}>
                  {status.consumerRunning ? t('showcase.rabbitmq.status.consumerRunningYes') : t('showcase.rabbitmq.status.consumerRunningNo')}
                </span>
              </InfoField>
              <InfoField label={t('showcase.rabbitmq.status.receivedCount')}>
                <code style={monoStyle}>{status.receivedCount}</code>
              </InfoField>
            </div>
            {!status.enabled && (
              <p style={{ marginTop: 'var(--spacing-3)', fontSize: 'var(--font-size-xs)', color: 'var(--color-warning)' }}>
                {t('showcase.rabbitmq.disabledNotice')}
              </p>
            )}
          </div>

          {/* publish */}
          <div style={cardStyle}>
            <h3 style={sectionTitleStyle}>{t('showcase.rabbitmq.publish.title')}</h3>
            <form onSubmit={handleSubmit(onSubmit)} noValidate style={{ display: 'flex', gap: 'var(--spacing-3)', flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <div style={{ flex: '1 1 240px' }}>
                <input
                  id="rabbitmq-message"
                  type="text"
                  placeholder={t('showcase.rabbitmq.publish.placeholder')}
                  aria-label={t('showcase.rabbitmq.publish.title')}
                  aria-invalid={!!errors.message}
                  aria-describedby={errors.message ? 'rabbitmq-message-error' : undefined}
                  disabled={disabled}
                  {...register('message')}
                  style={{
                    width: '100%',
                    padding: 'var(--spacing-3)',
                    border: `1px solid ${errors.message ? 'var(--color-danger)' : 'var(--color-surface-border)'}`,
                    borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--font-size-sm)',
                    fontFamily: 'var(--font-family-sans)',
                    background: disabled ? 'var(--color-surface-muted)' : 'var(--color-surface-background)',
                    color: 'var(--color-text-primary)',
                    boxSizing: 'border-box',
                  }}
                />
                {errors.message && (
                  <p id="rabbitmq-message-error" role="alert" style={errorTextStyle}>
                    {errors.message.message}
                  </p>
                )}
              </div>
              <button
                type="submit"
                disabled={disabled || !isValid || publishMutation.isPending}
                aria-busy={publishMutation.isPending}
                style={{
                  ...primaryButtonStyle,
                  opacity: disabled || publishMutation.isPending ? 0.6 : 1,
                  cursor: disabled || publishMutation.isPending ? 'not-allowed' : 'pointer',
                }}
              >
                {publishMutation.isPending ? t('showcase.rabbitmq.publish.sending') : t('showcase.rabbitmq.publish.button')}
              </button>
            </form>

            {/* publish — error */}
            {publishError && !publishMutation.isPending && (
              <div role="alert" style={{ marginTop: 'var(--spacing-3)', ...errorBoxStyle }}>
                {publishError.message ?? t('showcase.rabbitmq.publish.error')}
              </div>
            )}

            {/* publish — empty (아직 발행 전) */}
            {!publishMutation.data && !publishError && !publishMutation.isPending && (
              <p style={{ marginTop: 'var(--spacing-3)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                {t('showcase.rabbitmq.publish.empty')}
              </p>
            )}

            {/* publish — default (결과) */}
            {publishMutation.data && !publishMutation.isPending && (
              <div
                role="region"
                aria-label={t('showcase.rabbitmq.publish.resultLabel')}
                style={{ marginTop: 'var(--spacing-4)', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 'var(--spacing-3)' }}
              >
                <InfoField label={t('showcase.rabbitmq.publish.result.exchange')}>
                  <code style={monoStyle}>{publishMutation.data.exchange}</code>
                </InfoField>
                <InfoField label={t('showcase.rabbitmq.publish.result.routingKey')}>
                  <code style={monoStyle}>{publishMutation.data.routingKey}</code>
                </InfoField>
                <InfoField label={t('showcase.rabbitmq.publish.result.traceId')}>
                  <code style={monoStyle}>{publishMutation.data.traceId}</code>
                </InfoField>
                <InfoField label={t('showcase.rabbitmq.publish.result.publishedAt')}>
                  <code style={monoStyle}>{publishMutation.data.publishedAt}</code>
                </InfoField>
              </div>
            )}
          </div>

          {/* received */}
          <div style={cardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-3)' }}>
              <h3 style={{ ...sectionTitleStyle, marginBottom: 0 }}>{t('showcase.rabbitmq.received.title')}</h3>
              <button
                type="button"
                onClick={() => void receivedQuery.refetch()}
                disabled={receivedQuery.isFetching}
                style={secondaryButtonStyle}
              >
                {receivedQuery.isFetching ? t('showcase.rabbitmq.received.loading') : t('showcase.rabbitmq.received.refreshButton')}
              </button>
            </div>

            {receivedQuery.isFetching && (
              <p aria-live="polite" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
                {t('showcase.rabbitmq.received.loading')}
              </p>
            )}

            {receivedQuery.isError && !receivedQuery.isFetching && (
              <div role="alert" style={errorBoxStyle}>
                {(receivedQuery.error as ApiError)?.message ?? t('showcase.rabbitmq.received.error')}
              </div>
            )}

            {!receivedQuery.isFetching && !receivedQuery.isError && (!receivedQuery.data || receivedQuery.data.items.length === 0) && (
              <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                {t('showcase.rabbitmq.received.empty')}
              </p>
            )}

            {!receivedQuery.isFetching && receivedQuery.data && receivedQuery.data.items.length > 0 && (
              <div role="table" aria-label={t('showcase.rabbitmq.received.tableLabel')} style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-xs)' }}>
                  <thead>
                    <tr>
                      {[
                        t('showcase.rabbitmq.received.col.message'),
                        t('showcase.rabbitmq.received.col.routingKey'),
                        t('showcase.rabbitmq.received.col.queue'),
                        t('showcase.rabbitmq.received.col.traceId'),
                        t('showcase.rabbitmq.received.col.receivedAt'),
                      ].map((h) => (
                        <th
                          key={h}
                          scope="col"
                          style={{
                            padding: 'var(--spacing-2)',
                            textAlign: 'left',
                            borderBottom: '1px solid var(--color-surface-border)',
                            color: 'var(--color-text-secondary)',
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {receivedQuery.data.items.map((item, idx) => (
                      <tr key={`${item.routingKey}-${item.receivedAt}-${idx}`}>
                        <td style={tdStyle}>{item.message}</td>
                        <td style={{ ...tdStyle, fontFamily: 'var(--font-family-mono)' }}>{item.routingKey}</td>
                        <td style={{ ...tdStyle, fontFamily: 'var(--font-family-mono)' }}>{item.queue}</td>
                        <td style={{ ...tdStyle, fontFamily: 'var(--font-family-mono)' }}>{item.traceId}</td>
                        <td style={tdStyle}>{item.receivedAt}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Learning */}
      <div
        style={{
          marginTop: 'var(--spacing-4)',
          padding: 'var(--spacing-3)',
          background: 'var(--color-surface-elevated)',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-secondary)',
        }}
      >
        <span aria-hidden="true" style={{ marginRight: 'var(--spacing-2)' }}>📚</span>
        <strong>{t('showcase.panel.learning')}</strong> {t('showcase.rabbitmq.learning')}
      </div>
    </section>
  );
}

function InfoField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-1)' }}>{label}</p>
      <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)' }}>{children}</div>
    </div>
  );
}

const cardStyle: React.CSSProperties = {
  padding: 'var(--spacing-5)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  background: 'var(--color-surface-background)',
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: 'var(--font-size-base)',
  fontWeight: 700,
  color: 'var(--color-text-primary)',
  marginBottom: 'var(--spacing-4)',
};

const infoGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
  gap: 'var(--spacing-4)',
};

const monoStyle: React.CSSProperties = {
  fontFamily: 'var(--font-family-mono)',
  fontSize: 'var(--font-size-sm)',
};

const primaryButtonStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: 'var(--spacing-2) var(--spacing-4)',
  background: 'var(--color-brand-primary)',
  color: 'var(--color-text-inverse)',
  border: 'none',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 600,
  fontFamily: 'var(--font-family-sans)',
};

const secondaryButtonStyle: React.CSSProperties = {
  padding: 'var(--spacing-1) var(--spacing-3)',
  background: 'var(--color-surface-elevated)',
  color: 'var(--color-text-primary)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-xs)',
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
};

const errorTextStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-danger)',
  marginTop: 'var(--spacing-1)',
};

const errorBoxStyle: React.CSSProperties = {
  padding: 'var(--spacing-3)',
  border: '1px solid var(--color-danger)',
  borderRadius: 'var(--radius-md)',
  background: 'var(--color-surface-muted)',
  color: 'var(--color-danger)',
  fontSize: 'var(--font-size-sm)',
};

const tdStyle: React.CSSProperties = {
  padding: 'var(--spacing-2)',
  borderBottom: '1px solid var(--color-surface-border)',
  color: 'var(--color-text-primary)',
};
